import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'
import type { NotificationWithActor, NotificationSettings, NotificationFilter, NotificationType } from '@/types/notification'

export const NOTIFICATIONS_QUERY_KEY = 'notifications'

async function fetchNotifications(
  filter: NotificationFilter = {}
): Promise<NotificationWithActor[]> {
  const { user } = useAuthStore.getState()
  if (!user) throw new Error('User not authenticated')

  // notifications.user_id references auth.users(id), same as profiles.user_id — not profiles.id
  const authUserId = user.user_id

  let query = supabase
    .from('notifications')
    .select(`
      *,
      actor:profiles!notifications_actor_user_id_fkey(
        id,
        username,
        display_name,
        avatar_url
      )
    `)
    .eq('user_id', authUserId)
    .order('created_at', { ascending: false })

  if (filter.isRead !== undefined) {
    query = query.eq('is_read', filter.isRead)
  }

  if (filter.types && filter.types.length > 0) {
    query = query.in('type', filter.types)
  }

  if (filter.limit) {
    query = query.limit(filter.limit)
  }

  if (filter.offset) {
    query = query.range(filter.offset, filter.offset + (filter.limit || 20) - 1)
  }

  const { data, error } = await query

  if (error) throw error
  return data || []
}

async function fetchUnreadCount(): Promise<number> {
  const { user } = useAuthStore.getState()
  if (!user) return 0

  const { count, error } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', user.user_id)
    .eq('is_read', false)

  if (error) throw error
  return count || 0
}

async function markAsRead(notificationId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('id', notificationId)

  if (error) throw error
}

async function markAllAsRead(): Promise<void> {
  const { user } = useAuthStore.getState()
  if (!user) throw new Error('User not authenticated')

  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('user_id', user.user_id)
    .eq('is_read', false)

  if (error) throw error
}

async function deleteNotification(notificationId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .delete()
    .eq('id', notificationId)

  if (error) throw error
}

export function useNotifications(filter: NotificationFilter = {}) {
  return useQuery({
    queryKey: [NOTIFICATIONS_QUERY_KEY, filter],
    queryFn: () => fetchNotifications(filter),
  })
}

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: [NOTIFICATIONS_QUERY_KEY, 'unread-count'],
    queryFn: fetchUnreadCount,
    refetchInterval: 30000, // Poll every 30 seconds
  })
}

export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] })
    },
  })
}

export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] })
    },
  })
}

export function useDeleteNotification() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] })
    },
  })
}

// Notification settings hooks
async function fetchNotificationSettings(): Promise<NotificationSettings | null> {
  const { user } = useAuthStore.getState()
  if (!user) throw new Error('User not authenticated')

  const { data, error } = await supabase
    .from('notification_settings')
    .select('*')
    .eq('user_id', user.user_id)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    throw error
  }
  return data
}

async function updateNotificationSettings(settings: Partial<NotificationSettings>): Promise<NotificationSettings> {
  const { user } = useAuthStore.getState()
  if (!user) throw new Error('User not authenticated')

  const { data, error } = await supabase
    .from('notification_settings')
    .update(settings)
    .eq('user_id', user.user_id)
    .select()
    .single()

  if (error) throw error
  return data
}

export function useNotificationSettings() {
  return useQuery({
    queryKey: [NOTIFICATIONS_QUERY_KEY, 'settings'],
    queryFn: fetchNotificationSettings,
  })
}

export function useUpdateNotificationSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateNotificationSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY, 'settings'] })
    },
  })
}

/** Refetches notification queries when PostgREST inserts a row targeted at the signed-in user. */
export function useNotificationsInsertRealtime(enabled = true) {
  const qc = useQueryClient()
  const uid = useAuthStore((s) => s.user?.user_id)

  useEffect(() => {
    if (!enabled || !uid) return

    const channel = supabase
      .channel(`notifications-ins-${uid}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${uid}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] })
        },
      )
      .subscribe()

    return () => {
      void supabase.removeChannel(channel)
    }
  }, [enabled, uid, qc])
}